import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Briefcase, Filter } from 'lucide-react';
import { toast } from 'sonner';
import Layout from '../components/layout/Layout';
import ServiceCard from '../components/services/ServiceCard';
import NewsletterForm from '../components/common/NewsletterForm';
import { servicesAPI } from '../lib/api';
import { Badge } from '../components/ui/badge';

const serviceCategories = [
  { name: 'Todos', slug: '' },
  { name: 'Consultoria', slug: 'consultoria' },
  { name: 'Advocacia', slug: 'advocacia' },
  { name: 'Contabilidade', slug: 'contabilidade' },
  { name: 'Traduções', slug: 'traduções' },
  { name: 'Seguros', slug: 'seguros' },
];

export const ServicesPage = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('');

  const loadServices = useCallback(async () => {
    setLoading(true);
    try {
      const params = selectedCategory ? { category: selectedCategory } : {};
      const response = await servicesAPI.getAll(params);
      setServices(response.data);
    } catch (error) {
      console.error('Error loading services:', error);
      toast.error('Erro ao carregar serviços');
    } finally {
      setLoading(false);
    }
  }, [selectedCategory]);

  useEffect(() => {
    loadServices();
  }, [loadServices]);

  return (
    <Layout>
      {/* Header */}
      <section className="py-8 md:py-12 border-b border-[#D6D2C4]" data-testid="services-header">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-2 text-sm text-[#5C5C5C] font-['Inter'] mb-4">
            <Link to="/" className="hover:text-[#0F5A34]">Início</Link>
            <ChevronRight size={14} />
            <span className="text-[#1A1A1A]">Serviços Recomendados</span>
          </div>
          
          <div className="flex items-center gap-3 mb-4">
            <Briefcase size={32} className="text-[#0F5A34]" />
            <h1 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-[#1A1A1A]">
              Serviços Recomendados
            </h1>
          </div>
          
          <p className="text-[#5C5C5C] font-['Inter'] text-base max-w-2xl">
            Parceiros de confiança para ajudar você em todas as etapas da sua jornada em Portugal. Consultoria, advocacia, traduções e muito mais.
          </p>
        </div>
      </section>

      {/* Filter */}
      <section className="py-4 bg-white border-b border-[#D6D2C4]">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-4">
            <Filter size={18} className="text-[#5C5C5C]" />
            <div className="flex flex-wrap gap-2">
              {serviceCategories.map((cat) => (
                <Badge
                  key={cat.slug}
                  onClick={() => setSelectedCategory(cat.slug)}
                  className={`px-4 py-2 font-['Montserrat'] text-xs uppercase tracking-wider cursor-pointer transition-colors ${
                    cat.slug === selectedCategory
                      ? 'bg-[#0F5A34] text-white'
                      : 'bg-transparent border border-[#D6D2C4] text-[#5C5C5C] hover:border-[#0F5A34] hover:text-[#0F5A34]'
                  }`}
                  data-testid={`filter-${cat.slug || 'all'}`}
                >
                  {cat.name}
                </Badge>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12" data-testid="services-content">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-8">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="spinner" />
                </div>
              ) : services.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-[#5C5C5C] font-['Inter']">
                    Nenhum serviço encontrado nesta categoria.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {services.map((service) => (
                    <ServiceCard key={service.id} service={service} />
                  ))}
                </div>
              )}
            </div>

            {/* Sidebar */}
            <aside className="lg:col-span-4 space-y-6">
              <NewsletterForm variant="sidebar" />
            </aside>
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <NewsletterForm />
    </Layout>
  );
};

export default ServicesPage;
